import { Group, GradientStop, LinearGradient, Path } from "@progress/kendo-drawing";

import { CHART_COLORS } from "./chartConstants";

/*
 * Custom Kendo `visual` builders for the left-column dashboard charts.
 *
 * Kendo cannot express the designs' gradient-filled shapes through series
 * options, so the bars and the trend area are drawn by hand. This is the same
 * mechanism as createRoundedBarVisual in src/pages/eventDashboard/DashboardChart.jsx,
 * with the flat fill swapped for a LinearGradient.
 */

const BAR_CORNER_RADIUS = 4;
const BAR_CAP_HEIGHT = 4;

/*
 * Non-zero values whose bar rounds below this many pixels are bumped up to it,
 * so a small value next to a large one still shows a sliver rather than
 * vanishing. Zero stays invisible.
 */
const MIN_VISIBLE_BAR_LENGTH = 3;

/* Vertical gradient, top-to-bottom, relative to the shape's bounding box. */
const buildGradient = (stops) =>
  new LinearGradient({
    start: [0, 0],
    end: [0, 1],
    stops: stops.map((stop) => new GradientStop(stop.offset, stop.color, stop.opacity)),
  });

/* Rectangle with both top corners rounded and a flat bottom edge. */
const topRoundedPath = (fill, x, y, width, height, radius) => {
  const r = Math.min(radius, width / 2, height);

  return new Path({ fill, stroke: null })
    .moveTo(x, y + height)
    .lineTo(x, y + r)
    .arc(180, 270, r, r, false)
    .lineTo(x + width - r, y)
    .arc(270, 360, r, r, false)
    .lineTo(x + width, y + height)
    .close();
};

/**
 * Column bars for graph1.
 *
 * The design draws every bar as a faded gradient body; the bar for the
 * currently selected period is opaque, and the others carry a solid accent cap
 * across the top. `isSelected` receives the point's category so the caller can
 * decide without rebuilding the visual on every render.
 */
export const createGradientBarVisual =
  ({ stops, isSelected, fadedOpacity = 0.18, selectedOpacity = 0.95, cap = true }) =>
  (e) => {
    const value = Number(e.value);
    const hasNonZeroValue = Number.isFinite(value) && value !== 0;

    let { width, height } = e.rect.size;
    let { x, y } = e.rect.origin;

    if (hasNonZeroValue && height < MIN_VISIBLE_BAR_LENGTH) {
      // Keep the bar anchored to its baseline while growing it upward.
      y = y + height - MIN_VISIBLE_BAR_LENGTH;
      height = MIN_VISIBLE_BAR_LENGTH;
    }

    if (!width || !height) return e.createVisual();

    const selected = typeof isSelected === "function" ? isSelected(e) : Boolean(isSelected);
    const opacity = selected ? selectedOpacity : fadedOpacity;

    const body = topRoundedPath(
      buildGradient(stops),
      x,
      y,
      width,
      height,
      BAR_CORNER_RADIUS,
    );
    body.opacity(opacity);

    // The selected bar reads as "filled", so it drops the cap.
    if (selected || !cap) return body;

    const capHeight = Math.min(BAR_CAP_HEIGHT, height);
    const capPath = topRoundedPath(
      { color: CHART_COLORS.barCap },
      x,
      y,
      width,
      capHeight,
      BAR_CORNER_RADIUS,
    );

    return new Group().append(body, capPath);
  };

/**
 * Gradient fill under graph2's trend line.
 *
 * Kendo hands the area series its own polygon; we keep that geometry and only
 * restyle it, so the smooth (spline) edge is preserved.
 */
export const createGradientAreaVisual =
  ({ stops }) =>
  (e) => {
    const visual = e.createVisual();
    const gradient = buildGradient(stops);

    const applyFill = (node) => {
      if (typeof node.fill === "function" && node.options?.fill) {
        node.fill(gradient);
      }
      node.children?.forEach(applyFill);
    };

    applyFill(visual);

    return visual;
  };

/**
 * Horizontal stacked segments for graph3.
 *
 * Only the outermost segment of a row is rounded, so the stack reads as one
 * continuous bar rather than a row of separate pills.
 */
export const createStackedBarVisual =
  ({ radius = 6, roundStart = false, roundEnd = false }) =>
  (e) => {
    const { width, height } = e.rect.size;
    const { x, y } = e.rect.origin;

    if (!width || !height) return e.createVisual();

    const r = Math.min(radius, width / 2, height / 2);
    const path = new Path({ fill: { color: e.options.color }, stroke: null });

    path.moveTo(x + (roundStart ? r : 0), y);
    path.lineTo(x + width - (roundEnd ? r : 0), y);

    if (roundEnd) path.arc(270, 360, r, r, false);
    path.lineTo(x + width, y + height - (roundEnd ? r : 0));
    if (roundEnd) path.arc(0, 90, r, r, false);

    path.lineTo(x + (roundStart ? r : 0), y + height);
    if (roundStart) path.arc(90, 180, r, r, false);
    path.lineTo(x, y + (roundStart ? r : 0));
    if (roundStart) path.arc(180, 270, r, r, false);

    path.close();

    return path;
  };
